import React, { useState } from 'react'
import { Button } from '@wove/web-ui'
import { Settings, Eye, EyeOff, TestTube, CheckCircle, XCircle } from 'lucide-react'
import {
  useCargoWiseSettings,
  useUpdateCargoWiseSettings,
  useTestCargoWiseConnection,
} from '@wove/api'
import CargoWiseSettingsForm from './CargoWiseSettingsForm'
import { toast } from 'sonner'

interface CargoWiseSettingsProps {
  integrationId: string
}

export default function CargoWiseSettings({ integrationId }: CargoWiseSettingsProps) {
  const [showForm, setShowForm] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  const { data: settings, isLoading, error } = useCargoWiseSettings(integrationId)
  const updateMutation = useUpdateCargoWiseSettings(integrationId)
  const testConnectionMutation = useTestCargoWiseConnection(integrationId)

  const handleSave = async (formData: any) => {
    try {
      await updateMutation.mutateAsync(formData)
      setShowForm(false)
    } catch (error) {
      toast.error('Failed to save settings')
    }
  }

  const handleTestConnection = async () => {
    if (!settings?.credentials) return

    setTestResult(null)

    try {
      const result = await testConnectionMutation.mutateAsync({
        credentials: settings.credentials,
      })
      setTestResult(result)
    } catch (error: any) {
      const resultObj = error.response?.data
      setTestResult({
        success: false,
        message: resultObj?.message || 'Connection test failed. Please try again later.',
      })
    }
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Settings</h3>
            <p className="mt-2 text-sm text-red-700">
              Unable to load CargoWise settings. Please try refreshing the page.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading CargoWise settings...</span>
      </div>
    )
  }

  const hasCredentials = settings?.credentials

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Settings className="h-6 w-6 text-blue-600 mr-3" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">CargoWise Settings</h2>
            <p className="text-sm text-gray-600">
              Configure your CargoWise eAdaptor connection settings
            </p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          {hasCredentials ? 'Edit Settings' : 'Configure Settings'}
        </Button>
      </div>

      {/* Settings Display */}
      {hasCredentials ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                {settings.credentials.username}
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="flex items-center">
                <div className="flex-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {showPassword ? settings.credentials.password : '••••••••••••'}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  className="ml-2"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* eAdaptor URL */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">eAdaptor URL</label>
              <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                {settings.credentials.eAdaptorUrl}
              </div>
            </div>
          </div>

          {/* Test Connection */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Connection Test</h4>
                <p className="text-sm text-gray-500">
                  Verify that your credentials are working correctly
                </p>
              </div>
              <Button
                onClick={handleTestConnection}
                disabled={testConnectionMutation.isPending}
                variant="outline"
                className="flex items-center gap-2"
              >
                <TestTube className="h-4 w-4" />
                {testConnectionMutation.isPending ? 'Testing...' : 'Test Connection'}
              </Button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div
                className={`mt-3 p-3 rounded-md flex items-center ${
                  testResult.success
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-400 mr-2" />
                )}
                <span
                  className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}
                >
                  {testResult.message}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Settings className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No CargoWise Settings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure your CargoWise eAdaptor credentials to get started.
          </p>
          <div className="mt-6">
            <Button onClick={() => setShowForm(true)}>Configure CargoWise Settings</Button>
          </div>
        </div>
      )}

      {/* Settings Form Modal */}
      {showForm && (
        <CargoWiseSettingsForm
          settings={settings}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
          isSubmitting={updateMutation.isPending}
          integrationId={integrationId}
        />
      )}
    </div>
  )
}
